// ============================================
// DropCatch API Service - Full Implementation
// Covers all available API endpoints
// ============================================

import {
  AppConfig,
  Auction,
  AuctionBidsResult,
  AuctionSort,
  Backorder,
  BackorderSort,
  BackorderType,
  Bid,
  BidSort,
  BulkBackorderResult,
  BulkBidResult,
  CancelBackordersResult,
  DomainItem,
  History,
  HistorySearchItem,
  HistorySort,
  PlaceBid,
  UpsertBackorder,
  AuctionType,
} from "../types";

const DIRECT_API_BASE = "https://api.dropcatch.com";
const PROXY_API_BASE = "/api/dropcatch";

// In the browser (both dev and prod) we always go through the internal proxy/Edge function
// to avoid exposing DropCatch to CORS. Node environments can hit the API directly.
const isBrowser = typeof window !== "undefined";
const API_BASE = isBrowser ? PROXY_API_BASE : DIRECT_API_BASE;

export class DropCatchService {
  private config: AppConfig;
  private token: string | null = null;
  private tokenExpiry: number = 0;

  constructor(config: AppConfig) {
    this.config = config;
  }

  updateConfig(config: AppConfig) {
    this.config = config;
    // Reset token when config changes
    this.token = null;
    this.tokenExpiry = 0;
  }

  // ============================================
  // Authentication
  // ============================================

  private async authenticate(): Promise<string> {
    if (this.token && Date.now() < this.tokenExpiry) {
      return this.token;
    }

    if (!this.config.dropcatch_client_id || !this.config.dropcatch_client_secret) {
      throw new Error("Missing DropCatch Credentials. Please configure in Settings.");
    }

    try {
      const response = await fetch(`${API_BASE}/Authorize`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          ClientId: this.config.dropcatch_client_id,
          ClientSecret: this.config.dropcatch_client_secret,
        }),
      });

      if (!response.ok) {
        await this.logApiError(
          response,
          "authenticate with DropCatch",
          "Authentication failed. Verify your DropCatch Client ID/Secret and API base URL."
        );
      }

      const data = await response.json();

      if (data.IsError || data.Error) {
        console.error("[DropCatchService] Authentication response contained errors", data);
        throw new Error(data.ErrorDescription || data.Error || "Authentication failed");
      }

      this.token = data.token;
      // Token expires based on ExpiresIn, minus 60s buffer
      const expiresIn = data.expiresIn || 3600;
      this.tokenExpiry = Date.now() + expiresIn * 1000 - 60000;

      return this.token || "";
    } catch (error) {
      console.error("DropCatch Auth Error:", error);
      throw error;
    }
  }

  private async getHeaders(): Promise<HeadersInit> {
    const token = await this.authenticate();
    return {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  private async logApiError(
    response: Response,
    context: string,
    overrideMessage?: string
  ): Promise<never> {
    let body = "";
    try {
      body = await response.text();
    } catch (readError) {
      console.error(`[DropCatchService] Failed to read error body for ${context}`, readError);
    }

    const truncatedBody = body.length > 2000 ? `${body.slice(0, 2000)}...` : body;

    console.error(`[DropCatchService] ${context} request failed`, {
      status: response.status,
      statusText: response.statusText,
      body: truncatedBody,
    });

    const defaultMessage = `Failed to ${context}: ${response.status} ${response.statusText}`;
    throw new Error(overrideMessage || defaultMessage);
  }

  // ============================================
  // Client-side Filtering
  // ============================================

  private clientSideFilter(item: { Name?: string; Domain?: string }): boolean {
    const name: string = (item.Name || item.Domain || "").toLowerCase();
    const parts = name.split(".");
    const sld = parts.length > 1 ? parts.slice(0, -1).join(".") : name;

    if (this.config.no_dashes && sld.includes("-")) return false;
    if (this.config.no_numbers && /\d/.test(sld)) return false;
    if (this.config.min_length && sld.length < this.config.min_length) return false;
    if (this.config.max_length && sld.length > this.config.max_length) return false;

    if (this.config.required_keywords.length > 0) {
      const hasKeyword = this.config.required_keywords.some((k) =>
        name.includes(k.toLowerCase())
      );
      if (!hasKeyword) return false;
    }

    return true;
  }

  // ============================================
  // AUCTIONS API
  // ============================================

  /**
   * Get a single auction by ID
   */
  async getAuction(id: number): Promise<Auction> {
    const headers = await this.getHeaders();
    const response = await fetch(`${API_BASE}/v2/auctions/${id}`, { headers });

    if (!response.ok) {
      await this.logApiError(response, "get auction");
    }

    return response.json();
  }

  /**
   * Search auctions with filters
   */
  async searchAuctions(options?: {
    searchTerm?: string;
    size?: number;
    previous?: string;
    next?: string;
    showAllActive?: boolean;
    hasBids?: boolean | null;
    endTimeMin?: string;
    endTimeMax?: string;
    tlds?: string[];
    types?: AuctionType[];
    highBidMin?: number;
    highBidMax?: number;
    sort?: AuctionSort;
  }): Promise<{ items: DomainItem[]; previous?: string; next?: string }> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();

    // Apply options or use config defaults
    params.append("showAllActive", String(options?.showAllActive ?? true));
    params.append("size", String(options?.size || 50));

    if (options?.searchTerm) params.append("searchTerm", options.searchTerm);
    if (options?.previous) params.append("previous", options.previous);
    if (options?.next) params.append("next", options.next);
    if (options?.hasBids !== undefined && options?.hasBids !== null) {
      params.append("HasBids", String(options.hasBids));
    }
    if (options?.endTimeMin) params.append("EndTime.Min", options.endTimeMin);
    if (options?.endTimeMax) params.append("EndTime.Max", options.endTimeMax);
    if (options?.highBidMin) params.append("HighBid.Min", String(options.highBidMin));
    if (options?.highBidMax) params.append("HighBid.Max", String(options.highBidMax));
    if (options?.sort) params.append("sort", options.sort);

    // TLDs
    const tlds = options?.tlds || this.config.tlds;
    tlds.forEach((tld) => params.append("Tlds", tld));

    // Types
    const types = options?.types || this.config.auction_types;
    types.forEach((type) => params.append("Types", type));

    const response = await fetch(`${API_BASE}/v2/auctions?${params.toString()}`, {
      headers,
    });

    if (!response.ok) {
      if (response.status === 404) {
        return { items: [] };
      }
      await this.logApiError(response, "search auctions");
    }

    const data = await response.json();

    // API returns { items: [], totalRecords: N, cursor: { next?, previous? } }
    const rawItems: Auction[] = Array.isArray(data) ? data : data.items || [];

    // Get pagination tokens from cursor object in response body
    const previousToken = data.cursor?.previous || undefined;
    const nextToken = data.cursor?.next || undefined;

    const items: DomainItem[] = rawItems
      .filter((i) => this.clientSideFilter({ Name: i.name, Domain: i.name }))
      .map((i) => ({
        id: String(i.auctionId),
        name: i.name,
        status: i.type,
        endTime: i.endTime,
        price: i.highBid || 0,
        bidders: i.numberOfBidders || 0,
        type: "Auction" as const,
        winning: i.winning,
        maxBid: i.maxBid,
        minimumNextBid: i.minimumNextBid,
      }));

    return { items, previous: previousToken, next: nextToken };
  }

  /**
   * Multi-keyword search - searches for each keyword and deduplicates
   */
  async searchAuctionsMultiKeyword(): Promise<DomainItem[]> {
    const terms =
      this.config.search_keywords.length > 0 ? this.config.search_keywords : [""];
    const allItems: DomainItem[] = [];

    for (const term of terms) {
      try {
        const result = await this.searchAuctions({
          searchTerm: term,
          hasBids: this.config.has_bids,
          highBidMin: this.config.min_price || undefined,
          highBidMax: this.config.max_price || undefined,
        });
        allItems.push(...result.items);
      } catch (e) {
        console.error(`Error searching for term "${term}":`, e);
      }
    }

    // Deduplicate by domain name
    const unique = new Map<string, DomainItem>();
    allItems.forEach((item) => unique.set(item.name, item));
    return Array.from(unique.values());
  }

  /**
   * Get bid history for an auction
   */
  async getAuctionBidHistory(
    auctionId: number,
    options?: { size?: number; page?: number; sort?: string }
  ): Promise<AuctionBidsResult> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();

    if (options?.size) params.append("size", String(options.size));
    if (options?.page) params.append("page", String(options.page));
    if (options?.sort) params.append("sort", options.sort);

    const response = await fetch(
      `${API_BASE}/v2/auctions/${auctionId}/bids?${params.toString()}`,
      { headers }
    );

    if (!response.ok) {
      await this.logApiError(response, "get auction bid history");
    }

    return response.json();
  }

  // ============================================
  // BACKORDERS API
  // ============================================

  /**
   * Get list of backorders
   */
  async getBackorders(options?: {
    searchTerm?: string;
    size?: number;
    tld?: string;
    previous?: string;
    next?: string;
    type?: BackorderType;
    sort?: BackorderSort;
  }): Promise<{ items: Backorder[]; previous?: string; next?: string }> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();

    if (options?.searchTerm) params.append("searchTerm", options.searchTerm);
    if (options?.size) params.append("size", String(options.size));
    if (options?.tld) params.append("tld", options.tld);
    if (options?.previous) params.append("previous", options.previous);
    if (options?.next) params.append("next", options.next);
    if (options?.type) params.append("type", options.type);
    if (options?.sort) params.append("sort", options.sort);

    const response = await fetch(`${API_BASE}/v2/backorders?${params.toString()}`, {
      headers,
    });

    if (!response.ok) {
      if (response.status === 404) {
        return { items: [] };
      }
      await this.logApiError(response, "get backorders");
    }

    const data = await response.json();
    const items: Backorder[] = Array.isArray(data) ? data : data.items || [];

    return {
      items,
      previous: data.cursor?.previous || undefined,
      next: data.cursor?.next || undefined,
    };
  }

  /**
   * Create backorders for domains
   */
  async createBackorders(backorders: UpsertBackorder[]): Promise<BulkBackorderResult> {
    const headers = await this.getHeaders();

    const response = await fetch(`${API_BASE}/v2/backorders`, {
      method: "PUT",
      headers,
      body: JSON.stringify(backorders),
    });

    if (!response.ok) {
      await this.logApiError(response, "create backorders");
    }

    return response.json();
  }

  /**
   * Cancel backorders
   */
  async cancelBackorders(domains: string[]): Promise<CancelBackordersResult> {
    const headers = await this.getHeaders();

    const response = await fetch(`${API_BASE}/v2/backorders`, {
      method: "DELETE",
      headers,
      body: JSON.stringify(domains),
    });

    if (!response.ok) {
      await this.logApiError(response, "cancel backorders");
    }

    return response.json();
  }

  // ============================================
  // BIDS API
  // ============================================

  /**
   * Get your bids
   */
  async getBids(options?: {
    size?: number;
    previous?: string;
    next?: string;
    winning?: boolean;
    sort?: BidSort;
  }): Promise<{ items: Bid[]; previous?: string; next?: string }> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();

    if (options?.size) params.append("size", String(options.size));
    if (options?.previous) params.append("previous", options.previous);
    if (options?.next) params.append("next", options.next);
    if (options?.winning !== undefined) params.append("winning", String(options.winning));
    if (options?.sort) params.append("sort", options.sort);

    const response = await fetch(`${API_BASE}/v2/bids?${params.toString()}`, { headers });

    if (!response.ok) {
      await this.logApiError(response, "get bids");
    }

    const data = await response.json();
    const items: Bid[] = Array.isArray(data) ? data : data.items || [];

    return {
      items,
      previous: data.cursor?.previous || undefined,
      next: data.cursor?.next || undefined,
    };
  }

  /**
   * Place bids on auctions
   */
  async placeBids(bids: PlaceBid[]): Promise<BulkBidResult> {
    const headers = await this.getHeaders();

    const response = await fetch(`${API_BASE}/v2/bids`, {
      method: "POST",
      headers,
      body: JSON.stringify(bids),
    });

    if (!response.ok) {
      await this.logApiError(
        response,
        "place bids",
        response.status === 403
          ? "Bidding access denied. Account may be unverified or suspended."
          : undefined
      );
    }

    return response.json();
  }

  // ============================================
  // HISTORY API
  // ============================================

  /**
   * Get auction history
   */
  async getAuctionHistory(options?: {
    searchTerm?: string;
    size?: number;
    previous?: string;
    next?: string;
    sort?: HistorySort;
  }): Promise<{ items: History[]; previous?: string; next?: string }> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();

    if (options?.searchTerm) params.append("searchTerm", options.searchTerm);
    if (options?.size) params.append("size", String(options.size));
    if (options?.previous) params.append("previous", options.previous);
    if (options?.next) params.append("next", options.next);
    if (options?.sort) params.append("sort", options.sort);

    const response = await fetch(
      `${API_BASE}/v2/history/auctions?${params.toString()}`,
      { headers }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return { items: [] };
      }
      await this.logApiError(response, "get auction history");
    }

    const data = await response.json();
    const items: History[] = Array.isArray(data) ? data : data.items || [];

    return {
      items,
      previous: data.cursor?.previous || undefined,
      next: data.cursor?.next || undefined,
    };
  }

  /**
   * Get backorder history
   */
  async getBackorderHistory(options?: {
    searchTerm?: string;
    size?: number;
    previous?: string;
    next?: string;
    sort?: HistorySort;
  }): Promise<{ items: HistorySearchItem[]; previous?: string; next?: string }> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();

    if (options?.searchTerm) params.append("searchTerm", options.searchTerm);
    if (options?.size) params.append("size", String(options.size));
    if (options?.previous) params.append("previous", options.previous);
    if (options?.next) params.append("next", options.next);
    if (options?.sort) params.append("sort", options.sort);

    const response = await fetch(
      `${API_BASE}/v2/history/backorders?${params.toString()}`,
      { headers }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return { items: [] };
      }
      await this.logApiError(response, "get backorder history");
    }

    const data = await response.json();
    const items: HistorySearchItem[] = Array.isArray(data) ? data : data.items || [];

    return {
      items,
      previous: data.cursor?.previous || undefined,
      next: data.cursor?.next || undefined,
    };
  }

  // ============================================
  // Utility Methods
  // ============================================

  /**
   * Check if service is properly configured
   */
  isConfigured(): boolean {
    return !!(this.config.dropcatch_client_id && this.config.dropcatch_client_secret);
  }

  /**
   * Test connection to API
   */
  async testConnection(): Promise<{ success: boolean; message: string }> {
    try {
      await this.authenticate();
      return { success: true, message: "Successfully connected to DropCatch API" };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Connection failed",
      };
    }
  }
}
